// Deterministic Script v3 structural + referential validator (§9.9, revised).
// Enforces v3 invariants: plain-text content, single scene-level location,
// scene-level actors/props with materialized state, no legacy v2 fields
// (state_changes / action.actor_id / scene.context / speakers[] / content spans).
import { ACTION_TYPES, IMPORTANCE_TIERS, NON_ACTOR_SPEAKER_KINDS } from "./types.js";
import { canonicalNameKey } from "./names.js";
import { validateActionTimestampOrder } from "./timestamp.js";
import { isSceneTime } from "./types.js";
const isObj = (v) => typeof v === "object" && v !== null && !Array.isArray(v);
const isArr = (v) => Array.isArray(v);
const isStr = (v) => typeof v === "string";
const ACTION_LEGACY = ["state_changes", "actor_id", "speakers", "lines"];
export function validateScriptV3(doc) {
    const errors = [];
    const warnings = [];
    const err = (code, path, message, repairHint) => errors.push({ code, path, message, ...(repairHint ? { repair_hint: repairHint } : {}) });
    const warn = (code, path, message) => warnings.push({ code, path, message });
    if (!isObj(doc))
        return { ok: false, errors: [{ code: "not_object", path: "$", message: "script must be an object" }], warnings };
    if (doc.version !== 3)
        err("version", "$.version", "version must be 3");
    for (const key of ["title", "worldview", "style"])
        if (!isStr(doc[key]))
            err("field", `$.${key}`, `${key} must be a string`);
    const stateIds = new Map();
    const idKind = new Map();
    const actorNames = new Map();
    const collect = (list, kind, idField, nameField, path) => {
        if (!isArr(list)) {
            err("field", path, `${path} must be an array`);
            return;
        }
        list.forEach((raw, i) => {
            const p = `${path}[${i}]`;
            if (!isObj(raw)) {
                err("asset", p, "asset must be an object");
                return;
            }
            const id = raw[idField];
            if (!isStr(id)) {
                err("asset_id", `${p}.${idField}`, `${idField} required`);
                return;
            }
            if (idKind.has(id))
                err("dup_id", `${p}.${idField}`, `duplicate id ${id}`);
            idKind.set(id, kind);
            if (!isStr(raw[nameField]))
                err("asset_name", `${p}.${nameField}`, `${nameField} required`);
            else if (kind === "actor") {
                const nameKey = canonicalNameKey(raw[nameField]);
                const existingId = actorNames.get(nameKey);
                if (existingId !== undefined) {
                    err("dup_actor_name", `${p}.${nameField}`, `actor name ${raw[nameField]} already belongs to ${existingId}`);
                }
                else {
                    actorNames.set(nameKey, id);
                }
            }
            if (!isStr(raw.importance) || !IMPORTANCE_TIERS.includes(raw.importance))
                err("importance", `${p}.importance`, `importance must be one of ${IMPORTANCE_TIERS.join("|")}`);
            if (!isStr(raw.description))
                err("asset_desc", `${p}.description`, "description must be a string");
            const ids = new Set();
            const names = new Set();
            let defaults = 0;
            if (!isArr(raw.states) || raw.states.length === 0)
                err("states", `${p}.states`, "states must be a non-empty array");
            else
                raw.states.forEach((st, j) => {
                    const sp = `${p}.states[${j}]`;
                    if (!isObj(st) || !isStr(st.state_id)) {
                        err("state_id", sp, "state_id required");
                        return;
                    }
                    if (ids.has(st.state_id))
                        err("dup_state", sp, `duplicate state_id ${st.state_id}`);
                    ids.add(st.state_id);
                    if (st.state_id === "default")
                        defaults++;
                    if (!isStr(st.state_name))
                        err("state_name", `${sp}.state_name`, "state_name required");
                    else {
                        if (names.has(st.state_name))
                            err("dup_state_name", `${sp}.state_name`, `duplicate state_name ${st.state_name}`);
                        names.add(st.state_name);
                    }
                });
            if (isArr(raw.states) && defaults !== 1)
                err("default_state", `${p}.states`, `exactly one 'default' state required, found ${defaults}`);
            stateIds.set(id, ids);
        });
    };
    collect(doc.actors, "actor", "actor_id", "actor_name", "$.actors");
    collect(doc.locations, "location", "location_id", "location_name", "$.locations");
    collect(doc.props, "prop", "prop_id", "prop_name", "$.props");
    const refState = (assetId, stateId, kind, path) => {
        if (idKind.get(assetId) !== kind) {
            err("ref", path, `${kind} ${assetId} not found`);
            return;
        }
        if (stateId !== undefined && (!isStr(stateId) || !stateIds.get(assetId)?.has(stateId)))
            err("state_ref", path, `state ${String(stateId)} not materialized on ${assetId}`);
    };
    const checkEntityList = (list, kind, idField, path) => {
        if (list === undefined) {
            err("field", path, `${path} required (may be empty array)`);
            return;
        }
        if (!isArr(list)) {
            err("field", path, `${path} must be an array`);
            return;
        }
        list.forEach((ref, i) => {
            if (!isObj(ref) || !isStr(ref[idField])) {
                err("ref", `${path}[${i}]`, `${idField} required`);
                return;
            }
            refState(ref[idField], ref.state_id, kind, `${path}[${i}]`);
        });
    };
    const checkAction = (action, path, sceneActorIds, sceneAddress) => {
        if (!isObj(action)) {
            err("action", path, "action must be an object");
            return;
        }
        for (const bad of ACTION_LEGACY)
            if (bad in action)
                err("legacy_field", `${path}.${bad}`, `v3 forbids action.${bad}`);
        const type = action.type;
        if (!isStr(type) || !ACTION_TYPES.includes(type))
            err("action_type", `${path}.type`, `invalid action type ${String(type)}`);
        if (!isStr(action.content))
            err("content", `${path}.content`, "content must be a plain-text string");
        if (type === "dialogue" || type === "inner_thought") {
            const sp = action.speaker;
            if (!isObj(sp))
                err("speaker", `${path}.speaker`, "dialogue/inner_thought needs a speaker");
            else if ("kind" in sp) {
                if (!isStr(sp.kind) || !NON_ACTOR_SPEAKER_KINDS.includes(sp.kind))
                    err("speaker_kind", `${path}.speaker.kind`, "invalid non-actor speaker kind");
            }
            else if (isStr(sp.actor_id)) {
                refState(sp.actor_id, sp.state_id, "actor", `${path}.speaker`);
                if (!sceneActorIds.has(sp.actor_id)) {
                    const ownedStates = stateIds.get(sp.actor_id);
                    const requestedState = isStr(sp.state_id) && ownedStates?.has(sp.state_id)
                        ? sp.state_id
                        : ownedStates?.has("default")
                            ? "default"
                            : ownedStates?.values().next().value;
                    const repairHint = sceneAddress && requestedState
                        ? `scriptctl scene-ref ${sceneAddress} actor:${sp.actor_id} --state ${requestedState} -p "$SCRIPT"`
                        : undefined;
                    err("actor_not_in_scene", `${path}.speaker.actor_id`, `actor ${sp.actor_id} is not referenced by this scene`, repairHint);
                }
            }
            else
                err("speaker", `${path}.speaker`, "speaker needs actor_id or kind");
        }
    };
    const episodes = doc.episodes;
    if (!isArr(episodes))
        err("episodes", "$.episodes", "episodes must be an array");
    else
        episodes.forEach((ep, ei) => {
            const ep_p = `$.episodes[${ei}]`;
            if (!isObj(ep) || !isStr(ep.episode_id)) {
                err("episode_id", ep_p, "episode_id required");
                return;
            }
            if (!isArr(ep.scenes)) {
                err("scenes", `${ep_p}.scenes`, "scenes must be an array");
                return;
            }
            ep.scenes.forEach((sc, si) => {
                const sc_p = `${ep_p}.scenes[${si}]`;
                if (!isObj(sc)) {
                    err("scene", sc_p, "scene must be an object");
                    return;
                }
                if ("context" in sc)
                    err("legacy_field", `${sc_p}.context`, "v3 forbids scene.context");
                if (!isStr(sc.scene_id))
                    err("scene_id", sc_p, "scene_id required");
                const environment = sc.environment;
                if (environment === undefined || (isObj(environment) && (!isStr(environment.time) || environment.time.trim() === ""))) {
                    warn("scene_time_missing", `${sc_p}.environment.time`, "scene time is required for production review");
                }
                else if (!isObj(environment)
                    || (environment.space !== undefined && !isStr(environment.space))
                    || !isStr(environment.time)) {
                    err("environment", `${sc_p}.environment`, "environment must contain optional string space and a time string");
                }
                else if (!isSceneTime(environment.time)) {
                    err("scene_time", `${sc_p}.environment.time`, "scene time must be day|night|dawn|dusk");
                }
                const loc = sc.location;
                if (loc !== null) {
                    if (!isObj(loc) || !isStr(loc.location_id))
                        err("location", `${sc_p}.location`, "location must be null or {location_id}");
                    else
                        refState(loc.location_id, loc.state_id, "location", `${sc_p}.location`);
                }
                checkEntityList(sc.actors, "actor", "actor_id", `${sc_p}.actors`);
                checkEntityList(sc.props, "prop", "prop_id", `${sc_p}.props`);
                if (isArr(sc.props)) {
                    const propIds = new Set();
                    sc.props.forEach((ref, i) => {
                        if (!isObj(ref) || !isStr(ref.prop_id))
                            return;
                        if (propIds.has(ref.prop_id))
                            err("dup_prop_ref", `${sc_p}.props[${i}]`, `duplicate prop ref ${ref.prop_id}`);
                        propIds.add(ref.prop_id);
                    });
                }
                const sceneActorIds = new Set(isArr(sc.actors)
                    ? sc.actors
                        .filter((ref) => isObj(ref))
                        .map((ref) => ref.actor_id)
                        .filter((id) => isStr(id))
                    : []);
                if (!isArr(sc.actions))
                    err("actions", `${sc_p}.actions`, "actions must be an array");
                else {
                    const sceneAddress = isStr(sc.scene_id) ? `${ep.episode_id}/${sc.scene_id}` : undefined;
                    sc.actions.forEach((a, ai) => checkAction(a, `${sc_p}.actions[${ai}]`, sceneActorIds, sceneAddress));
                    validateActionTimestampOrder(sc.actions, `${sc_p}.actions`, err);
                }
            });
        });
    return { ok: errors.length === 0, errors, warnings };
}
